import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { TopBar } from "../components/layout/TopBar";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Spinner } from "../components/shared/Spinner";
import { Button } from "../components/shared/Button";
import { EmptyState } from "../components/shared/EmptyState";
import { CollapsibleSection } from "../components/shared/CollapsibleSection";
import * as ui from "../styles/ui";
import {
  api,
  type GitDocEntry,
  type GitStatus,
  type ConfigStatus,
  type SanityDashboardFamily,
} from "../api/client";
import { useGitStore } from "../stores/useGitStore";
import { useNotionStore } from "../stores/useNotionStore";
import { useToastStore } from "../stores/useToastStore";
import { useBatchStore } from "../stores/useBatchStore";
import { formatApiError } from "../lib/api-errors";
import { useShelfRole } from "../lib/use-shelf-role";
import { publishDocToBatch, promoteToReview } from "../lib/publish-doc";
import { docxToMarkdown } from "../lib/docx-to-md";
import type { ImportedDocLocationState } from "./DocumentEditorPage";
import { stringifyFrontMatter } from "../lib/front-matter";
import { versionSortKey } from "../../shared/versions";
import { websiteSlug, slugify, docPath } from "../../shared/doc-paths";
import { normalizeDocStatus, isReleaseEligible } from "../../shared/types";

const PUBLIC_STATIC_BASE = "https://adjacent.markets/static";
// Public site slug mirrors the worker (shared websiteSlug helper): an explicit
// front-matter slug wins, else the family with the "adjacent-" prefix dropped.
const publicPath = (slug: string) => `/static/${slug}`;
const publicUrl = (slug: string) => `${PUBLIC_STATIC_BASE}/${slug}`;
// Defaults for docs created via bulk .docx upload.
const INITIAL_VERSION = "v0.1";
const UPLOAD_DOC_TYPE = "pdfDocument";
const PAGE_SIZE = 10;

// One public /static/ URL cell, shared by every dashboard table.
function PublicUrlCell({ slug }: { slug: string }) {
  if (!slug) return <td style={ui.td} />;
  return (
    <td style={ui.td}>
      <a
        href={publicUrl(slug)}
        target="_blank"
        rel="noreferrer"
        style={styles.urlLink}
      >
        {publicPath(slug)}
      </a>
    </td>
  );
}

// Per-table search box (documents / pre-release).
function TableSearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      spellCheck={false}
      style={styles.tableSearch}
    />
  );
}

// Prev/next pager shown under a paginated table; hidden when a single page.
function Pager({
  page,
  pageCount,
  total,
  onPrev,
  onNext,
}: {
  page: number;
  pageCount: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (pageCount <= 1) return null;
  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  return (
    <div style={styles.pager}>
      <Button onClick={onPrev} disabled={page === 0}>
        prev
      </Button>
      <span style={styles.pagerText}>
        {from}–{to} of {total}
      </span>
      <Button onClick={onNext} disabled={page >= pageCount - 1}>
        next
      </Button>
    </div>
  );
}

export function DashboardIntro() {
  return (
    <div style={styles.intro}>
      <p style={styles.introText}>
        Shelf is Adjacent&apos;s document pipeline. Import Notion drafts, edit
        and version the markdown in git, then publish approved methodology and
        factsheets to Sanity.
      </p>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const shelfRole = useShelfRole();
  const [gitDocs, setGitDocs] = useState<GitDocEntry[]>([]);
  const [gitStatus, setGitStatus] = useState<GitStatus | null>(null);
  const [integrations, setIntegrations] = useState<ConfigStatus | null>(null);
  const [sanityFamilies, setSanityFamilies] = useState<SanityDashboardFamily[]>(
    [],
  );
  const [loadingSanity, setLoadingSanity] = useState(true);
  const [sanityError, setSanityError] = useState<string | null>(null);
  const [loadingGit, setLoadingGit] = useState(true);
  const { searchQuery } = useNotionStore();
  const addToast = useToastStore((s) => s.addToast);
  const batchId = useBatchStore((s) => s.batchId);
  const stageInBatch = useBatchStore((s) => s.stage);
  const refreshBatch = useBatchStore((s) => s.refresh);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [publishing, setPublishing] = useState<{
    done: number;
    total: number;
  } | null>(null);
  // Per-table search, separate from the global palette search.
  const [docQuery, setDocQuery] = useState("");
  const [preQuery, setPreQuery] = useState("");
  const [docPage, setDocPage] = useState(0);
  const [prePage, setPrePage] = useState(0);
  const uploadRef = useRef<HTMLInputElement>(null);
  const [converting, setConverting] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const fetchTree = useGitStore((s) => s.fetchTree);
  const fetchGitStatus = useGitStore((s) => s.fetchStatus);

  const loadGit = useCallback(() => {
    setLoadingGit(true);
    Promise.all([fetchGitStatus(), fetchTree(true)])
      .then(([status, docs]) => {
        setGitStatus(status);
        setGitDocs(docs);
      })
      .catch(() => {
        setGitStatus(null);
        setGitDocs([]);
      })
      .finally(() => setLoadingGit(false));
  }, [fetchGitStatus, fetchTree]);

  const loadSanity = useCallback(() => {
    setLoadingSanity(true);
    setSanityError(null);
    api.sanity
      .dashboard()
      .then((data) => setSanityFamilies(data.families))
      .catch((e) => {
        const msg =
          e instanceof Error ? e.message : "Failed to load Sanity documents";
        setSanityError(msg);
        setSanityFamilies([]);
      })
      .finally(() => setLoadingSanity(false));
  }, []);

  const refreshAll = useCallback(() => {
    loadGit();
    loadSanity();
  }, [loadGit, loadSanity]);

  const deleteSanityFamily = useCallback(
    (slug: string | null, title: string) => {
      if (!slug) return;
      if (
        !window.confirm(
          `Delete "${title}" from Sanity? This removes the orphaned draft and cannot be undone.`,
        )
      ) {
        return;
      }
      api.sanity
        .remove({ familySlug: slug })
        .then(() => {
          addToast("deleted from Sanity", "success");
          loadSanity();
        })
        .catch((e) => addToast(formatApiError(e), "error"));
    },
    [addToast, loadSanity],
  );

  useEffect(() => {
    api.config
      .status()
      .then(setIntegrations)
      .catch(() => setIntegrations(null));
    loadGit();
    loadSanity();
  }, [loadGit, loadSanity]);

  useEffect(() => {
    setDocPage(0);
  }, [docQuery]);
  useEffect(() => {
    setPrePage(0);
  }, [preQuery]);

  const q = searchQuery.trim().toLowerCase();

  // ── Sanity documents (source of truth: methodology/factsheet only) ──
  // Each family's status is whatever Sanity says (current version, else latest).
  const sanitySlugs = new Set(
    sanityFamilies.map((f) => f.slug).filter((s): s is string => !!s),
  );
  const sanityRows = sanityFamilies
    .map((f) => {
      const current = f.currentVersion
        ? f.versions.find((v) => v.versionString === f.currentVersion)
        : undefined;
      const shown = current ?? f.versions[0];
      return {
        family: f,
        version: shown?.versionString ?? f.currentVersion ?? "—",
        status: (shown?.status ?? "draft") as
          "draft" | "published" | "superseded",
      };
    })
    .filter(
      ({ family: f }) =>
        !q ||
        f.title.toLowerCase().includes(q) ||
        (f.slug ?? "").toLowerCase().includes(q),
    );
  const publishedCount = sanityRows.filter(
    (r) => r.status === "published",
  ).length;
  const draftCount = sanityRows.filter((r) => r.status === "draft").length;

  // ── Pre-release: things in git/notion that are NOT in Sanity yet ──
  // Group repo docs by family, then keep only families whose slug is not
  // present in Sanity.
  const families = new Map<string, GitDocEntry[]>();
  for (const doc of gitDocs) {
    const list = families.get(doc.family) ?? [];
    list.push(doc);
    families.set(doc.family, list);
  }
  const todayStr = new Date().toISOString().split("T")[0];
  const nonSanityRows = [...families.entries()]
    // The git family key keeps its type-dir prefix (e.g. "methodologies/<slug>"),
    // but Sanity stores the bare slug — compare the last path segment so a
    // published family isn't also listed as pre-release.
    .filter(([fam]) => !sanitySlugs.has(fam.split("/").pop() ?? fam))
    .map(([fam, docs]) => {
      const sorted = [...docs].sort(
        (a, b) => versionSortKey(a.version) - versionSortKey(b.version),
      );
      const latest = sorted[sorted.length - 1];
      // ISO yyyy-mm-dd strings compare lexically.
      const reviewOverdue = !!latest.nextReview && latest.nextReview < todayStr;
      return { family: fam, docs: sorted, latest, reviewOverdue };
    })
    .filter(({ family: fam, docs }) => {
      if (!q) return true;
      return (
        fam.toLowerCase().includes(q) ||
        docs.some((d) => d.title.toLowerCase().includes(q))
      );
    });
  // A doc flipped to published in git (on main) belongs in "documents" even if
  // Sanity doesn't (yet) hold the family — the published state is the signal,
  // not which store currently has it. Everything else stays pre-release.
  const gitPublishedRows = nonSanityRows.filter(
    (r) => r.latest.status === "published",
  );
  const familyRows = nonSanityRows.filter(
    (r) => r.latest.status !== "published",
  );
  const overdueCount = familyRows.filter((r) => r.reviewOverdue).length;
  const documentCount = sanityRows.length + gitPublishedRows.length;
  const totalPublished = publishedCount + gitPublishedRows.length;

  // ── Per-table search (matches title / family / slug / url) ──
  const dq = docQuery.trim().toLowerCase();
  const pq = preQuery.trim().toLowerCase();
  const shownGitPublishedRows = gitPublishedRows.filter(
    ({ family, latest }) =>
      !dq ||
      (latest.title || family).toLowerCase().includes(dq) ||
      family.toLowerCase().includes(dq) ||
      websiteSlug(latest.slug, family).toLowerCase().includes(dq),
  );
  const shownSanityRows = sanityRows.filter(
    ({ family }) =>
      !dq ||
      family.title.toLowerCase().includes(dq) ||
      (family.slug ?? "").toLowerCase().includes(dq),
  );
  const shownDocumentCount =
    shownGitPublishedRows.length + shownSanityRows.length;
  const shownFamilyRows = familyRows.filter(
    ({ family, docs, latest }) =>
      !pq ||
      (latest.title || family).toLowerCase().includes(pq) ||
      family.toLowerCase().includes(pq) ||
      docs.some((d) => d.title.toLowerCase().includes(pq)),
  );

  // ── Pagination (10 rows per table, keeps both tables a consistent height) ──
  const docPageCount = Math.max(1, Math.ceil(shownDocumentCount / PAGE_SIZE));
  const prePageCount = Math.max(
    1,
    Math.ceil(shownFamilyRows.length / PAGE_SIZE),
  );
  const docPageSafe = Math.min(docPage, docPageCount - 1);
  const prePageSafe = Math.min(prePage, prePageCount - 1);
  const docStart = docPageSafe * PAGE_SIZE;
  // Documents render as gitPublished rows followed by sanity rows; slice the
  // combined window so each page holds 10 across both groups.
  const pagedGitPublishedRows = shownGitPublishedRows.slice(
    docStart,
    docStart + PAGE_SIZE,
  );
  const pagedSanityRows = shownSanityRows.slice(
    Math.max(0, docStart - shownGitPublishedRows.length),
    Math.max(0, docStart + PAGE_SIZE - shownGitPublishedRows.length),
  );
  const pagedFamilyRows = shownFamilyRows.slice(
    prePageSafe * PAGE_SIZE,
    prePageSafe * PAGE_SIZE + PAGE_SIZE,
  );

  // ── Bulk publish (pre-release → FAB batch) ──
  const latestByFamily = new Map(familyRows.map((r) => [r.family, r.latest]));
  const toggleSelect = (fam: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fam)) next.delete(fam);
      else next.add(fam);
      return next;
    });
  };
  // Pre-release docs eligible for bulk review+publish: drafts (auto-promoted to
  // review) and docs already in review.
  const eligibleStatus = (s: string) =>
    isReleaseEligible(normalizeDocStatus(s));
  const selectedEligible = [...selected].filter((fam) => {
    const latest = latestByFamily.get(fam);
    return latest && eligibleStatus(latest.status);
  });

  // Promote any selected drafts to review, then publish each to Sanity, staging
  // every change onto the batch so they all land in the one floating PR.
  const handleBulkReviewPublish = async () => {
    if (selectedEligible.length === 0 || publishing) return;
    const targets = selectedEligible
      .map((fam) => latestByFamily.get(fam))
      .filter((d): d is GitDocEntry => !!d);
    setPublishing({ done: 0, total: targets.length });
    let done = 0;
    let failed = 0;
    for (const doc of targets) {
      try {
        if (normalizeDocStatus(doc.status) === "draft") {
          await promoteToReview(doc.path, batchId);
        }
        const result = await publishDocToBatch(doc.path, batchId);
        for (const p of result.staged) stageInBatch(p);
        if (result.status === "done") done += 1;
        else {
          failed += 1;
          addToast(`${result.title}: ${result.detail ?? "failed"}`, "error");
        }
      } catch (e) {
        failed += 1;
        addToast(
          `${doc.title}: ${e instanceof Error ? e.message : "failed"}`,
          "error",
        );
      }
      setPublishing((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
    }
    setPublishing(null);
    setSelected(new Set());
    void refreshBatch();
    refreshAll();
    addToast(
      `published ${done}${failed ? `, failed ${failed}` : ""}`,
      failed ? "error" : "success",
    );
  };

  // Upload .docx → markdown. A single file opens the editor for review; multiple
  // files are converted and committed straight onto the batch draft branch so
  // they all land in one PR.
  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return;

    if (files.length === 1) {
      setConverting({ done: 0, total: 1 });
      try {
        const { markdown, warnings } = await docxToMarkdown(files[0]);
        const baseName = files[0].name.replace(/\.docx$/i, "").trim();
        if (warnings.length > 0) {
          addToast(
            `converted with ${warnings.length} formatting warning${warnings.length !== 1 ? "s" : ""}`,
            "info",
          );
        }
        const state: ImportedDocLocationState = {
          importedMarkdown: markdown,
          importedTitle: baseName,
          importedFamily: slugify(baseName),
        };
        navigate("/doc/new", { state });
      } catch (e) {
        addToast(
          e instanceof Error ? e.message : "failed to convert .docx",
          "error",
        );
      } finally {
        setConverting(null);
      }
      return;
    }

    setConverting({ done: 0, total: files.length });
    const today = new Date().toISOString().split("T")[0];
    let staged = 0;
    let failed = 0;
    for (const file of files) {
      try {
        const { markdown } = await docxToMarkdown(file);
        const baseName = file.name.replace(/\.docx$/i, "").trim();
        const family = slugify(baseName);
        const content = stringifyFrontMatter(
          {
            doc_type: UPLOAD_DOC_TYPE,
            family,
            version: INITIAL_VERSION,
            status: "draft",
            title: baseName,
            effective_date: today,
            template: UPLOAD_DOC_TYPE,
            slug: websiteSlug("", family),
          },
          markdown,
        );
        const path = docPath(UPLOAD_DOC_TYPE, family, INITIAL_VERSION);
        await api.git.commit({
          path,
          content,
          message: `add: ${baseName} ${INITIAL_VERSION}`,
          action: "save",
          batch: batchId,
        });
        stageInBatch(path);
        staged += 1;
      } catch (e) {
        failed += 1;
        addToast(
          `${file.name}: ${e instanceof Error ? e.message : "failed"}`,
          "error",
        );
      }
      setConverting((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
    }
    setConverting(null);
    void refreshBatch();
    refreshAll();
    addToast(
      `staged ${staged}${failed ? `, failed ${failed}` : ""} for PR`,
      failed ? "error" : "success",
    );
  };

  const loading = loadingGit && loadingSanity;
  const gitConnected = gitStatus?.connected === true;
  const notionConfigured = integrations?.notion === true;
  const sanityConfigured = integrations?.sanity === true;

  return (
    <>
      <TopBar
        title="dashboard"
        meta={
          gitConnected && gitStatus?.url ? (
            <>
              <a
                href={gitStatus.url}
                target="_blank"
                rel="noreferrer"
                style={styles.repoLink}
              >
                {gitStatus.repo}
              </a>
              {" · "}
              {gitStatus.defaultBranch}
            </>
          ) : undefined
        }
        actions={
          <Button onClick={refreshAll} disabled={loadingGit}>
            refresh
          </Button>
        }
      />
      <DashboardIntro />
      <div style={ui.pageContent}>
        {sanityError && sanityConfigured && (
          <div style={styles.errorLine}>{sanityError}</div>
        )}

        <div style={styles.statsRow}>
          {documentCount} document{documentCount !== 1 ? "s" : ""}
          {" · "}
          {totalPublished} published · {draftCount} draft
          {" · "}
          {familyRows.length} pre-release
          {overdueCount > 0 && (
            <>
              {" · "}
              {overdueCount} review{overdueCount !== 1 ? "s" : ""} overdue
            </>
          )}
        </div>

        {loading && sanityRows.length === 0 && familyRows.length === 0 && (
          <div style={styles.loadingWrap}>
            <Spinner size={28} label="loading…" />
          </div>
        )}

        {/* ── Documents: Sanity is the source of truth (methodology / factsheet) ── */}
        <CollapsibleSection label="documents">
          {documentCount > 0 && (
            <TableSearchInput
              value={docQuery}
              onChange={setDocQuery}
              placeholder={`search ${documentCount} document${documentCount !== 1 ? "s" : ""}…`}
            />
          )}
          {shownDocumentCount > 0 ? (
            <>
              <table style={{ ...ui.table, marginBottom: 28 }}>
                <thead>
                  <tr>
                    <th style={ui.thFirst}>title</th>
                    <th style={{ ...ui.th, width: 120 }}>type</th>
                    <th style={{ ...ui.th, width: 100 }}>version</th>
                    <th style={{ ...ui.th, width: 110 }}>status</th>
                    <th style={ui.th}>url</th>
                    <th style={{ ...ui.th, width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {pagedGitPublishedRows.map(({ family, latest }) => (
                    <tr key={family} style={ui.row}>
                      <td style={{ ...ui.tdFirst, ...styles.tdName }}>
                        <button
                          onClick={() =>
                            navigate(`/family/${encodeURIComponent(family)}`)
                          }
                          style={styles.pageLink}
                        >
                          {latest.title || family}
                        </button>
                      </td>
                      <td style={ui.td}>
                        <span style={ui.metaText}>{latest.docType}</span>
                      </td>
                      <td style={ui.td}>
                        <span style={ui.metaText}>{latest.version}</span>
                      </td>
                      <td style={ui.td}>
                        <StatusBadge status="published" />
                      </td>
                      <PublicUrlCell slug={websiteSlug(latest.slug, family)} />
                      <td style={ui.td} />
                    </tr>
                  ))}
                  {pagedSanityRows.map(({ family, version, status }) => (
                    <tr key={family.id} style={ui.row}>
                      <td style={{ ...ui.tdFirst, ...styles.tdName }}>
                        <button
                          onClick={() =>
                            family.slug &&
                            navigate(
                              `/family/${encodeURIComponent(family.slug)}`,
                            )
                          }
                          style={styles.pageLink}
                        >
                          {family.title}
                        </button>
                      </td>
                      <td style={ui.td}>
                        <span style={ui.metaText}>{family.docType}</span>
                      </td>
                      <td style={ui.td}>
                        <span style={ui.metaText}>{version}</span>
                      </td>
                      <td style={ui.td}>
                        <StatusBadge
                          status={
                            status as
                              "draft" | "review" | "published" | "superseded"
                          }
                        />
                      </td>
                      <PublicUrlCell
                        slug={
                          family.slug ? websiteSlug(undefined, family.slug) : ""
                        }
                      />
                      <td style={ui.td}>
                        {status !== "published" && shelfRole === "admin" && (
                          <button
                            onClick={() =>
                              deleteSanityFamily(family.slug, family.title)
                            }
                            style={styles.deleteLink}
                            title="delete orphaned draft from Sanity"
                          >
                            delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager
                page={docPageSafe}
                pageCount={docPageCount}
                total={shownDocumentCount}
                onPrev={() => setDocPage(Math.max(0, docPageSafe - 1))}
                onNext={() =>
                  setDocPage(Math.min(docPageCount - 1, docPageSafe + 1))
                }
              />
            </>
          ) : dq && documentCount > 0 ? (
            <EmptyState title={`no documents match "${docQuery}"`} />
          ) : sanityConfigured && !loadingSanity ? (
            <EmptyState
              title={
                q
                  ? `no documents match "${searchQuery}"`
                  : "no methodology or factsheet documents in sanity"
              }
              body={
                q ? undefined : "publish a document to sanity to see it here"
              }
            />
          ) : null}
        </CollapsibleSection>

        {/* ── Pre-release: in git / notion but not yet in Sanity ── */}
        <CollapsibleSection label="pre-release">
          {(notionConfigured || (gitConnected && !loadingGit)) && (
            <div style={styles.preReleaseBar}>
              {notionConfigured && (
                <Button onClick={() => navigate("/import")}>import</Button>
              )}
              {gitConnected && !loadingGit && (
                <>
                  <Button
                    onClick={() => navigate("/doc/new?template=methodology")}
                  >
                    new
                  </Button>
                  <input
                    ref={uploadRef}
                    type="file"
                    multiple
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      e.target.value = "";
                      if (files.length > 0) void handleUpload(files);
                    }}
                  />
                  <Button
                    onClick={() => uploadRef.current?.click()}
                    disabled={!!converting}
                  >
                    {converting
                      ? converting.total > 1
                        ? `converting ${converting.done}/${converting.total}…`
                        : "converting…"
                      : "upload"}
                  </Button>
                </>
              )}
              {selectedEligible.length > 0 && (
                <Button
                  onClick={handleBulkReviewPublish}
                  disabled={!!publishing}
                >
                  {publishing
                    ? `publishing ${publishing.done}/${publishing.total}…`
                    : `review & publish (${selectedEligible.length})`}
                </Button>
              )}
            </div>
          )}
          {familyRows.length > 0 && (
            <TableSearchInput
              value={preQuery}
              onChange={setPreQuery}
              placeholder={`search ${familyRows.length} pre-release…`}
            />
          )}
          {shownFamilyRows.length > 0 ? (
            <>
              <table style={{ ...ui.table, marginBottom: 28 }}>
                <thead>
                  <tr>
                    <th style={{ ...ui.th, width: 32 }} />
                    <th style={ui.thFirst}>title</th>
                    <th style={{ ...ui.th, width: 110 }}>versions</th>
                    <th style={{ ...ui.th, width: 100 }}>latest</th>
                    <th style={{ ...ui.th, width: 110 }}>status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFamilyRows.map(
                    ({ family, docs, latest, reviewOverdue }) => {
                      const eligible = eligibleStatus(latest.status);
                      return (
                        <tr key={family} style={ui.row}>
                          <td style={ui.td}>
                            <input
                              type="checkbox"
                              checked={selected.has(family)}
                              disabled={!eligible || !!publishing}
                              onChange={() => toggleSelect(family)}
                              title={
                                eligible
                                  ? "select to review & publish"
                                  : "cannot publish this status"
                              }
                              style={styles.checkbox}
                            />
                          </td>
                          <td style={{ ...ui.tdFirst, ...styles.tdName }}>
                            <button
                              onClick={() =>
                                navigate(
                                  `/family/${encodeURIComponent(family)}`,
                                )
                              }
                              style={styles.pageLink}
                            >
                              {latest.title || family}
                            </button>
                            {reviewOverdue && (
                              <span
                                style={styles.reviewDot}
                                title="review overdue"
                              />
                            )}
                          </td>
                          <td style={ui.td}>{docs.length}</td>
                          <td style={ui.td}>
                            <span style={ui.metaText}>{latest.version}</span>
                          </td>
                          <td style={ui.td}>
                            <StatusBadge
                              status={
                                latest.status as
                                  | "draft"
                                  | "review"
                                  | "published"
                                  | "superseded"
                              }
                            />
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
              <Pager
                page={prePageSafe}
                pageCount={prePageCount}
                total={shownFamilyRows.length}
                onPrev={() => setPrePage(Math.max(0, prePageSafe - 1))}
                onNext={() =>
                  setPrePage(Math.min(prePageCount - 1, prePageSafe + 1))
                }
              />
            </>
          ) : pq && familyRows.length > 0 ? (
            <EmptyState title={`no pre-release docs match "${preQuery}"`} />
          ) : null}
        </CollapsibleSection>
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "40vh",
  },
  intro: {
    padding: "0 var(--page-pad-x) var(--space-3)",
    marginBottom: "var(--space-4)",
  },
  introText: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--fg-2)",
    lineHeight: 1.6,
    margin: 0,
    maxWidth: 720,
  },
  errorLine: {
    color: "var(--comp-salmon)",
    fontSize: 12,
    marginBottom: 16,
  },
  repoLink: {
    color: "var(--fg-1)",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  statsRow: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    marginBottom: 16,
  },
  tableSearch: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--fg-1)",
    background: "transparent",
    border: "1px solid var(--comp-rule)",
    padding: "10px 12px",
    marginBottom: 12,
    width: "100%",
    boxSizing: "border-box" as const,
    outline: "none",
  },
  preReleaseBar: {
    display: "flex",
    gap: 8,
    marginBottom: 12,
  },
  pager: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: -16,
    marginBottom: 28,
    fontFamily: "var(--font-mono)",
    fontSize: 12,
  },
  pagerText: {
    color: "var(--fg-2)",
  },
  tdName: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  pageLink: {
    color: "var(--fg-1)",
    fontWeight: 600,
    fontSize: 13,
    textDecoration: "none",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    textAlign: "left" as const,
  },
  reviewDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "var(--comp-salmon)",
    marginLeft: 6,
    flexShrink: 0,
  },
  deleteLink: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--comp-salmon)",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },
  urlLink: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
    textDecoration: "underline",
    textUnderlineOffset: 2,
    wordBreak: "break-all" as const,
  },
  checkbox: {
    cursor: "pointer",
    accentColor: "var(--comp-green, #2e7d32)",
  },
};
